/**
 * Generate a random, secure-ish string of characters. Since we're
 * not using OpenSSL, this is not necessarily a cryptographically-secure
 * string. You should generate those server side.
 * @see https://gist.github.com/6174/6062387
 * @param count the number of characters to generate
 * @param [chars='abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'] the characters to use
 * @returns a string of random characters
 */
export const random = (function () {
    const buffer = new Uint8Array(32);
    let index;
    let bitIndex;
    const max = BigInt(buffer.length);

    const reset = function () {
        index = 0n;
        bitIndex = 0n;
        crypto.getRandomValues(buffer);
    };
    reset();

    const getBits = function (count) {
        let bits = 0n;
        while (count > 0n) {
            const todo = count < 8n - bitIndex ? count : 8n - bitIndex;
            count -= todo;
            bits = bits << todo;
            bits += (BigInt(buffer[index]) >> bitIndex) & ((1n << todo) - 1n);
            bitIndex += todo;
            if (bitIndex === 8n) {
                bitIndex = 0n;
                index++;
            }
            if (index === max) {
                reset();
            }
        }
        return bits;
    };

    const countBits = function (num) {
        let bitCount = 0n;
        while (num > 0n) {
            bitCount++;
            num = num >> 1n;
        }
        return bitCount;
    };

    const getN = function (max, bitCount) {
        if (max <= 0n) {
            throw new Error(
                "this does not compute unless you want an infinite loop"
            );
        }
        let out;

        do {
            out = getBits(bitCount);
        } while (out >= max);

        return out;
    };

    return function (
        count,
        input = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ"
    ) {
        let wasNumber = false;
        let wasString = false;

        switch (typeof input) {
            default: {
                throw new Error("unsupported input");
            }
            case "number": {
                wasNumber = true;
                input = BigInt(input);
            }
            case "bigint": {
                const out = getN(input, countBits(max));
                return wasNumber ? Number(out) : out;
            }
            case "string": {
                wasString = true;
                input = input.split("");
            }
            case "object": {
                if (!Array.isArray(input)) {
                    throw new Error("objects are not supported here");
                }
                if (typeof count != "number" && typeof count != "bigint") {
                    throw new Error("you need to specify a count");
                }
                const contentCount = BigInt(input.length);
                const bitCount = countBits(contentCount);
                const out = [...Array(count)].map((_) => {
                    return input[getN(contentCount, bitCount)];
                });
                return wasString ? out.join("") : out;
            }
        }
    };
})();
